import { Alignment, Api, Currency, DateUtil, ModelRow } from "@mcleod/core";
import {
    Label, Layout, SlideoutDecorator, TableRow, TableRowDisplayEvent, TableSelectionEvent
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { FilterPanel } from "@mcleod/dispatch";
import { AutogenLayoutOrderPayablesProcess } from "./autogen/AutogenLayoutOrderPayablesProcess";
import { ManageRecurringPayAdjustmentProps, ManageRecurringPayAdjustments } from "./ManageRecurringPayAdjustments";
import { OrderPayablesTabLayout, PayablesLookup } from "./OrderPayables";
import { OrderPayablesProcessStepper } from "./OrderPayablesProcessStepper";
import { OrderPayablesTaskFilter } from "./OrderPayablesTaskFilter";
import { OrderPayablesUtil } from "./OrderPayablesUtil";
import { RowOrderPayablesProfile } from "./models/ModelOrderPayablesProfile";


export class OrderPayablesProcess extends AutogenLayoutOrderPayablesProcess implements OrderPayablesTabLayout {
    public tpFilterPanel: FilterPanel;
    public filterPanel: FilterPanel;
    public getProfileRow: () => RowOrderPayablesProfile;
    public getPayablesLookupMap: () => PayablesLookup;
    public selectedGroupByRows: TableRow[] = [];
    public selectedGroupByValue: string = "";
    columnManager: ColumnVisibilityManager;

    onLoad(): void | Promise<void> {
        this.tpFilterPanel = new FilterPanel([OrderPayablesTaskFilter.TP_PAID], this.tableOrderPayables, this.panelTpTaskFilters);
        this.filterPanel = new FilterPanel([OrderPayablesTaskFilter.SUMMARY_PROCESS, OrderPayablesTaskFilter.PAYMENT_PROCESS], this.tableOrderPayables, this.panelTaskFilters);
        this.tableOrderPayables.addRowDisplayListener((event: TableRowDisplayEvent) => this.tableOrderPayablesOnRowDisplay(event));
        this.columnManager = new ColumnVisibilityManager(this.tableOrderPayables, this.layoutOrderPayablesToolPanel);
        this.panelTpTaskFilters.visible = false;
        this.setupFilterHandlers();
        this.setInitialColumnVisibility();
    }

    private setInitialColumnVisibility(): void {
        this.columnManager.updateVisibility({
            showType: true,
            showPaid: false
        });
    }

    private setupFilterHandlers(): void {
        const paidFilter = this.filterPanel.filters.find(f => f.id === "paid");
        const submittedToTriumphPayFilter = this.filterPanel.filters.find(f => f.id === "submittedToTriumphPay");
        if (paidFilter) {
            // Set up click handler for paid filter
            paidFilter.button.addClickListener(() => {
                this.updateColumnVisibility();
            });
        }
        if(submittedToTriumphPayFilter) {
            // Set up click handler for paid filter
            submittedToTriumphPayFilter.button.addClickListener(() => {
                this.updateColumnVisibility();
            });
        }

        // Set up listeners for all filters to ensure proper visibility reset
        this.filterPanel.filters.forEach(filter => {
            if (filter.id !== "paid" && filter.id !== "submittedToTriumphPay") {
                filter.button.addClickListener(() => {
                    if (this.filterPanel.getActiveFilter()?.id !== "paid") {
                        this.columnManager.updateVisibility({
                            showType: true,
                            showPaid: false
                        });
                    }
                });
            }
        });
    }



    public updateColumnVisibility(): void {
        const isPaidActive = this.filterPanel.getActiveFilter()?.id === "paid";
        const isSubmittedToTriumphPayActive = this.filterPanel.getActiveFilter()?.id === "submittedToTriumphPay";
        if(isPaidActive) {
            this.columnManager.updateVisibility({
                showType: !isPaidActive,
                showPaid: isPaidActive
            });
        }
        else if(isSubmittedToTriumphPayActive) {
            this.columnManager.updateVisibility({
                showType: false,
                showPaid: false
            });
        }
        else{
            this.columnManager.updateVisibility({
                showType: true,
                showPaid: false
            });
        }
    }

    getLayoutBaseFilter(row: ModelRow): boolean {
        return row.get("ready_to_pay_flag") == "Y";
    }

    tableOrderPayablesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();

        if (!tableRow.data.get("triumphpay_active", false)) {
            this.panelTaskFilters.paddingLeft = 4;
            this.panelTpTaskFilters.visible = false;
        }else{
            this.panelTaskFilters.paddingLeft = 0;
            this.panelTpTaskFilters.visible = true;
        }

        tableRow.forEveryChildComponent((component) => {
            OrderPayablesUtil.setTooltipContent(component, tableRow.data);
        });
        OrderPayablesUtil.setComponentDetails({
            tableRow: tableRow,
            profileRow: this.getProfileRow()
        });
    }

    tableOrderPayablesOnSelect(event: TableSelectionEvent) {
        const selectedRows = this.tableOrderPayables.selectedRows;
        const someFreezePaySelected = selectedRows.some(row => !row.data.isNull("freeze_by"));
        this.buttonProcess.enabled = selectedRows.length > 0 && !someFreezePaySelected;
        if (someFreezePaySelected) {
            this.buttonProcess.disabledTooltip = "Cannot process frozen payables";
        }
        this.buttonProcess.caption = "Process (" + selectedRows.length + ")";
    }

    public resetProcessButtonCaption() {
        const selectedRows = this.tableOrderPayables.selectedRows;
        this.buttonProcess.caption = "Process (" + selectedRows.length + ")";
    }

    async orderPayablesProcess() {
        this.buttonProcess.busy = true;
        const shouldContinue = await this.checkRecurringPayAdjustments();
        if (!shouldContinue) {
            this.buttonProcess.busy = false;
            return;
        }

        const settlementTotal = new Currency({currency_code: this.getProfileRow().get("currency_type"), amount: 0});
        const selectedRows = this.tableOrderPayables.selectedRows;
        const selectedPayables = this.getSettlementsAndAdjustments(selectedRows);
        selectedPayables.forEach(row => settlementTotal.add(row.getCurrency("total_pay")));

        const payeeIds = new Set();
        selectedPayables.forEach(row => payeeIds.add(row.get("payee_id")));
        const payeeIdArray = Array.from(payeeIds);

        const payeeTypes = new Set();
        selectedPayables.forEach(row => payeeTypes.add(row.get("payee_type")));
        const payeeTypeArray = Array.from(payeeTypes);

        const checkNumbers = new Set();
        selectedPayables.forEach(row => {
            if (row.get("check_number") != undefined) {
                checkNumbers.add(row.get("check_number"))
            }
        });
        const checkNumbersArray = Array.from(checkNumbers);

        let latestOk2PayDate = null;
        selectedPayables.forEach(row => {
            const ok2PayDate: Date = DateUtil.justDate(DateUtil.parseDate(row.get("ok2pay_date"))) as Date;
            if (!latestOk2PayDate || ok2PayDate > latestOk2PayDate) {
                latestOk2PayDate = ok2PayDate;
            }
        });
        const selectedSettlements = selectedPayables.filter(row => row.get("record_type") == "S");
        const settlementAdjustments = this.getAdjustmentIdsFromSettlements(selectedSettlements);
        const deductions = selectedPayables.filter(row => row.get("record_type") == "P" && !settlementAdjustments.includes(row.get("record_id"))).map(row => row.get("record_id")).concat(settlementAdjustments);
        const totalPay = new Currency({
            currency_code: this.getProfileRow().get("currency_type"),
            amount: selectedPayables.reduce((sum, row: ModelRow) => sum + row.getCurrency("pay_amount", 0).getAmount(), 0)
        });
        const profileRow = this.getProfileRow();
        OrderPayablesProcessStepper.showAsSlideout({
            title: "Process Order Payables (" + selectedRows.length + ") " + new Currency(totalPay),
            settlementIds: selectedSettlements.map(row => row.get("record_id")).join(","),
            deductionIds: deductions.join(","),
            checkNumbers: checkNumbersArray.join(","),
            checkNumberCount: checkNumbersArray.length,
            endDate:  profileRow.get("cutoff_date") == undefined ? DateUtil.parseDate(new Date()):  DateUtil.parseDateWithKeywords(profileRow.get("cutoff_date"), true, false),
            cutoffDate: latestOk2PayDate,
            payeeIds: payeeIdArray.join(","),
            payeeTypes: payeeTypeArray.join(","),
            profileRow: this.getProfileRow(),
            processCount: selectedRows.length,
            selectedPayables: selectedPayables,
            doAfterSlideOut: () => {
                this.tableOrderPayables.selectedRows = null;
                this.tableOrderPayablesOnSelect(null);
            },
            total: totalPay
        }).finally(() => {
            this.buttonProcess.busy = false;
        });
    }

    private async checkRecurringPayAdjustments(): Promise<boolean> {
        const response = await Api.search("lme/settlement/manage-recurring-pay-adjustments", this.getManageRecurringProps());
        if (response?.data?.length > 0) {
            const isYes = await CommonDialogs.showYesNo("There are recurring pay adjustments that have not been processed for this pay period. Would you like to process them now?", "Manage Recurring Pay Adjustments");
            if (isYes) {
                this.buttonManageRecurringOnClick();
                return Promise.resolve(false);
            } else if (isYes === undefined) {
                return Promise.resolve(false);
            }
        }
        return Promise.resolve(true);
    }

    private getManageRecurringProps(): ManageRecurringPayAdjustmentProps {
        const cutoffDate = DateUtil.parseDateWithKeywords(this.getProfileRow().get("cutoff_date"));
        return {
            date: cutoffDate ?? new Date(),
            currency_code: this.getProfileRow().get("currency_type"),
            payee_type: this.getProfileRow().get("payee_type"),
            payee_status: this.getProfileRow().get("payee_status"),
            payees: this.getProfileRow().get("payee_ids"),
            payees_exclude: this.getProfileRow().getBoolean("payee_ids_exclude", false),
            departments: this.getProfileRow().get("departments"),
            departments_exclude: this.getProfileRow().getBoolean("departments_exclude", false),
            frozen_payables: this.getProfileRow().get("frozen_payables"),
            payment_method: this.getProfileRow().get("payment_method"),
            payment_recipient: this.getProfileRow().get("payment_recipient")
        }
    }

    buttonManageRecurringOnClick() {
        const layout = Layout.getLayout("lme/settlement/ManageRecurringPayAdjustments") as ManageRecurringPayAdjustments;
        SlideoutDecorator.decorateAndSlideIn(layout, {
            width: "100%",
            fillVerticalSpace: true,
            title: "Manage Recurring Pay Adjustments",
            transitionOptions: {
                direction: Alignment.BOTTOM
            },
            layoutLoadListeners: () => {
                layout.props = this.getManageRecurringProps();
                layout.buttonSearch.clicked();
            }
        });
    }

    getSettlementsAndAdjustments(selectedRows: TableRow[]) {
        const records : ModelRow[] = [];
        for (const row of selectedRows) {
            records.push(row.data);
            if (row.data.get("record_type") == "S") {
                OrderPayablesUtil.getRelatedPayAdjustmentRows(row.data, this.getPayablesLookupMap()).forEach((adjustment) => {
                    const passesGroupBY = this.selectedGroupByRows.length == 0 || this.selectedGroupByRows.some((tableRow: TableRow) => tableRow.data.get("group_by_value") == adjustment.get(this.selectedGroupByValue));
                    const rolledUp = OrderPayablesUtil.settlementInTabWithAdjustment(adjustment, this.getPayablesLookupMap());
                    if (passesGroupBY && rolledUp) {
                        records.push(adjustment);
                    }
                });
            }
        }
        return records;
    }

    getAdjustmentIdsFromSettlements(settlements: ModelRow[]) : String[] {
        const adjustments : ModelRow[] = [];
        for (const settlement of settlements) {
            OrderPayablesUtil.getRelatedPayAdjustmentRows(settlement, this.getPayablesLookupMap()).forEach((adjustment) => {
                const passesGroupBY = this.selectedGroupByRows.length == 0 || this.selectedGroupByRows.some((tableRow: TableRow) => tableRow.data.get("group_by_value") == adjustment.get(this.selectedGroupByValue));
                const rolledUp = OrderPayablesUtil.settlementInTabWithAdjustment(adjustment, this.getPayablesLookupMap());
                if (passesGroupBY && rolledUp) {
                    adjustments.push(adjustment);
                }
            });
        }
        return adjustments.filter(row => row.get("ready_to_pay_flag") == "Y").map(row => row.get("record_id"));
    }
}

class ColumnVisibilityManager {
    constructor(
        private readonly table: any,
        private readonly toolPanel: any
    ) { }

    private updateColumnVisibility(caption: string, visible: boolean): void {
        const column = this.table.getColumnByCaption(caption);
        if (column) {
            this.table.setColumnVisibility(column, visible);
        }
    }

   updateVisibility(state: ColumnVisibilityState): void {
           if (!this.table) return;

           // Handle type column visibility - hide when paid filter is active
           const typeColumn = this.table.getColumnByCaption("Type");
           if (typeColumn) {
               this.table.setColumnVisibility(typeColumn, state.showType);
           }

           // Regular columns are always visible unless paid filter or submitted filter is active
           const regularColumns = [
               "columnHeaderRecordDetails",
               "columnHeaderOkToPayDate",
               "columnHeaderInvoiceNumber",
               "columnHeaderStatus",
               "columnHeaderSettlementPay",
               "columnHeaderPayAdjustment",
               "columnHeaderWarning",
           ];

           regularColumns.forEach(column => {
               this.updateColumnVisibility(column, !state.showPaid);
           });


           // Paid-specific columns - only show when paid filter is active
           const paidColumns = [
               "columnHeaderPaymentId",
               "columnHeaderProcessDate",
               "columnHeaderTransactionDate",
               "columnHeaderTransactionNumber",
               "columnHeaderCarrierPayDate",
               "columnHeaderConfirmationCode",
               "columnHeaderPaymentDetails",
               "columnHeaderQuickPay"
           ];

           paidColumns.forEach(column => {
               this.updateColumnVisibility(column, state.showPaid);
           });


           // Update cells visibility if rows exist
           if (this.table.rows) {
               this.updateCellsVisibility(state);
           }
       }public updateCellsVisibility(state: ColumnVisibilityState): void {
           if (!this.table?.rows) return;
           this.table.rows.forEach(row => {
               const regularCells = [
                   { id: "TypeCell", type: Label },
                   { id: "recordDetailsCell", type: Label },
                   { id: "okToPayCell", type: Label },
                   { id: "invoiceCell", type: Label },
                   { id: "processStatusCell", type: Label },
                   { id: "payAmountCell", type: Label },
                   { id: "payAdjustmentCell", type: Label },
                   { id: "adjustmentWarningCell", type: Image },
               ];

               const paidCells = [
                   { id: "paymentIdCell", type: Label },
                   { id: "transactionNumberCell", type: Label },
                   { id: "transactionDateCell", type: Label },
                   { id: "quickPayCell", type: Label },
                   { id: "processDateCell", type: Label },
                   { id: "carrierPayDateCell", type: Label },
                   { id: "confirmationCodeCell", type: Label },
                   { id: "paymentDetailsCell", type: Label }

               ];

               // Show/hide regular cells
               regularCells.forEach(cell => {
                   this.updateCell(row, cell.id, cell.type, !state.showPaid);
               });

               // Show/hide paid cells
               paidCells.forEach(cell => {
                   this.updateCell(row, cell.id, cell.type, state.showPaid);
               });
           });
       }

    private updateCell(row: any, cellId: string, componentType: any, isVisible: boolean): void {
        const cell = row.cells.find(c => c.id === cellId);
        if (cell) {
            cell.visible = isVisible;
            const component = cell.components.find(c => c instanceof componentType);
            if (component) {
                component.visible = isVisible;
            }
        }
    }
}

interface ColumnVisibilityState {
    showType: boolean;
    showPaid: boolean;
}
